import _ from 'lodash';
import appFeatures from 'effective-features!';

import UnderwritingIssueDE from './errors/UnderwritingIssueDE';
import FieldValidationIssueDE from './errors/FieldValidationIssueDE';
import GenericValidationIssueDE from './errors/GenericValidationIssueDE';
import { LEVEL_ERROR, UNDERWRITING_ISSUE } from './errors/DisplayableError';

export {
    FIELD_VALIDATION,
    GENERIC_VALIDATION,
    UNDERWRITING_ISSUE,
    LEVEL_ERROR,
    LEVEL_WARN
} from './errors/DisplayableError';


function removeDuplicatePropertiesFromUnderwritingIssue(uwIssue) {
    return _.omit(uwIssue, ['onOfferings', 'hasApprovalOrRejection', 'currentBlockingPoint', 'offerings']);
}

function backendUwIssueToOfferingIssues(uwIssue) {
    const { onOfferings: offeringIssues } = uwIssue;
    const cleanUwIssue = removeDuplicatePropertiesFromUnderwritingIssue(uwIssue);

    return offeringIssues
        .map(offeringIssue => Object.assign({}, cleanUwIssue, offeringIssue, {originalDTO: uwIssue}));
}

function parseUnderwritingIssues(uwIssues) {
    const offeringIssues = uwIssues.flatMap(uwIssue => backendUwIssueToOfferingIssues(uwIssue));
    return offeringIssues.map(offeringIssue => new UnderwritingIssueDE(offeringIssue));
}

function toErrorList({validationIssues = {}, underwritingIssues = []}, flowValidationStepMapping) {

    const fieldValidationIssues = _.get(validationIssues, 'fieldIssues', []);
    const genericValidationIssues = _.get(validationIssues, 'issues', []);

    return parseUnderwritingIssues(underwritingIssues)
        .concat(fieldValidationIssues.map(fValIssue => new FieldValidationIssueDE(fValIssue, flowValidationStepMapping)))
        .concat(genericValidationIssues.map(valIssue => new GenericValidationIssueDE(valIssue, flowValidationStepMapping)));
}


function mergeDisplayableErrors(newErrorList, oldErrorList = []) {
    const comparingStrategy = (err1, err2) => {
        return (err1.id === err2.id);
    };
    const findInOldList = error => oldErrorList.find(existingError => comparingStrategy(error, existingError));
    const withMergingStrategy = error => {
        // retain the old error if already existing
        if (error.type === UNDERWRITING_ISSUE) {
            return error;
        }
        return findInOldList(error) || error;
    };

    return newErrorList
        .map(withMergingStrategy); // retain the old error
}

const getLast = arr => arr[arr.length - 1];

/**
 * A stateful service that holds information about the validation of the current flow
 */
class FlowValidationService {

    constructor(flowValidationStepMapping = []) {
        this.flowValidationStepMapping = flowValidationStepMapping;
        /** @member {DisplayableError[]}*/
        this.errorsAndWarnings = [];
        this.watchers = new Set();
        this._visitedSteps = [];
        this.getLatestFlowStep = this.getLatestFlowStep.bind(this);
        this.transitionListener = this.transitionListener.bind(this);
    }

    _notify() {
        const displayableError = this.getDisplayableErrors();
        Array.from(this.watchers)
            .forEach(subscriber => {
                subscriber(displayableError);
            });
    }

    getLatestFlowStep() {
        return _.last(this._visitedSteps);
    }

    isActive() { // eslint-disable-line class-methods-use-this
        return !!appFeatures.flowStepsValidationEnabled;
    }

    /**
     * Stores information about the visited steps in a flow
     * @param {Object} toStep destination step for the transition
     */
    transitionListener(toStep) {
        this._visitedSteps = [...this._visitedSteps, toStep.name];
    }

    /**
     * Adds a new observer to be notified when validation errors change
     * @param {function} subscriber the function to be invoked when theo validation errors change
     * @returns {function} a handle to the observer for later removal
     */
    subscribeToChanges(subscriber) {
        this.watchers.add(subscriber);
        return subscriber;
    }

    /**
     * Removes an observer from the listeners of validation errors changes
     * @param {function} subscriber the subscriber that needs to be removed
     */
    unsubscribeFromChanges(subscriber) {
        this.watchers.delete(subscriber);
    }

    reportErrorsAndWarnings(errorsAndWarnings = {}) {
        if (!this.isActive()) {
            return;
        }
        const errorList = toErrorList(_.cloneDeep(errorsAndWarnings), this.flowValidationStepMapping);

        // deduplicate
        this.errorsAndWarnings = mergeDisplayableErrors(errorList, this.errorsAndWarnings);
        this._notify;
    }

    getDisplayableErrors({
        types = [],
        level = null,
        includeHidden = false,
        includeErrorsOnCurrentStep = false
    } = {}) {
        const isOfType = (error, type) => error.type === type;
        const hasVisitedStep = step => this._visitedSteps.includes(step);
        const isVisibleUWError = error => error.type === UNDERWRITING_ISSUE && !error.hasApprovalOrRejection;
        const hasJustLandedOnStep = step => getLast(this._visitedSteps) === step && this._visitedSteps.filter(visited => visited === step).length === 1;
        const isStepRelevant = step => (hasVisitedStep(step) && (includeErrorsOnCurrentStep || !hasJustLandedOnStep(step)));
        const relevantTypes = Array.isArray(types) ? types : [types];
        const filterByType = error => relevantTypes.length === 0 || relevantTypes.some(type => isOfType(error, type));
        const filterByVisitedSteps = error => isVisibleUWError(error) || _.isEmpty(error.wizardSteps) || error.wizardSteps.some(isStepRelevant);
        const filterByErrorLevel = error => !level || level === error.level;
        const filterByHiddenStatus = error => includeHidden || !error.isHidden;

        return this.errorsAndWarnings
            .filter(filterByType)
            .filter(filterByErrorLevel)
            .filter(filterByVisitedSteps)
            .filter(filterByHiddenStatus);
    }

    acknowledgeAllWarnings({type = null} = {}) {
        // errors are blocking, whilst warnings are not
        const canBeHidden = (error) => (error.level !== LEVEL_ERROR && (!type || error.type === type));
        this.errorsAndWarnings = this.errorsAndWarnings
            .map(error => {
                if (canBeHidden(error)) {
                    return Object.assign({}, error, {isHidden: true});
                }
                return error;
            });
        this._notify();
    }

    /**
     * Returns an ordered list of steps which have validation/underwriting issues
     * Steps are ordered according to the flowValidationStepMapping defined in flowStepToFieldsMappings.json5
     * ONLY the visited steps will be returned.
     *
     * @returns {String[]} an ordered list of visited steps that have validation errors
     */
    getInvalidWizardSteps({types, level, includeCurrentStep} = {}) {
        const invalidStepsFromErrors = this.getDisplayableErrors({types, level, includeErrorsOnCurrentStep: includeCurrentStep})
            .flatMap(err => err.wizardSteps);

        const potentialStepsWithValidationErrors = this.flowValidationStepMapping
            .map(flowStep => flowStep.name)
            .filter(flowStepName => invalidStepsFromErrors.includes(flowStepName));

        return potentialStepsWithValidationErrors
            .filter(step => this._visitedSteps.includes(step));
    }

    getFirstInvalidStep({types, level, includeCurrentStep} = {}) {
        const invalidSteps = this.getInvalidWizardSteps({types, level, includeCurrentStep});
        if (!_.isEmpty(invalidSteps)) {
            return _.first(invalidSteps);
        }
        return this.getLatestFlowStep();
    }

    acknowledgeWarning(id) {
        // errors are blocking, whilst warnings are not
        const canBeHidden = (error) => (!error.blockingAt && error.id === id);
        this.errorsAndWarnings = this.errorsAndWarnings
            .map(error => {
                if (canBeHidden(error)) {
                    return Object.assign({}, error, {isHidden: true});
                }
                return error;
            });
        this._notify();
    }

    approveUnderwritingIssue(id) {
        this.errorsAndWarnings = this.errorsAndWarnings
            .map(error => {
                if (error.type === UNDERWRITING_ISSUE && error.id === id) {
                    return Object.assign({}, error, {hasApprovalOrRejection: true});
                }
                return error;
            });
        this._notify();
    }

}

export default FlowValidationService;